/**
 * HAD Clothing - Main JavaScript
 * Core functionality and utilities for HAD custom sections
 */

// Utility Functions
const HADUtils = {
  // Debounce function for performance
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  // Throttle function for scroll/resize events
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // Smooth scroll to element
  smoothScrollTo(target, offset = 0) {
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    if (!element) return;

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  },

  // Format currency
  formatCurrency(amount, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  },

  // Check if element is in viewport
  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  },

  // Lazy load images
  lazyLoadImages() {
    const images = document.querySelectorAll('img[data-src]');

    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            img.classList.add('loaded');
            observer.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback for older browsers
      images.forEach(img => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
    }
  }
};

// Scroll Animations
class ScrollAnimations {
  constructor() {
    this.animatedElements = document.querySelectorAll('[data-scroll-animation]');
    if (this.animatedElements.length > 0) {
      this.init();
    }
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animateElement(entry.target);
              this.observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -50px 0px'
        }
      );

      this.animatedElements.forEach(el => this.observer.observe(el));
    } else {
      // Fallback: animate immediately
      this.animatedElements.forEach(el => this.animateElement(el));
    }
  }

  animateElement(element) {
    const animationType = element.dataset.scrollAnimation || 'fadeInUp';
    element.style.animation = `${animationType} 0.6s ease-out`;
    element.classList.add('animated');
  }
}

// Smooth Scroll for Anchor Links
class SmoothScroll {
  constructor() {
    this.links = document.querySelectorAll('a[href^="#"]');
    if (this.links.length > 0) {
      this.init();
    }
  }

  init() {
    this.links.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href === '#') return;

        const target = document.querySelector(href);
        if (target) {
          e.preventDefault();
          HADUtils.smoothScrollTo(target, 80);

          // Update URL without jumping
          if (history.pushState) {
            history.pushState(null, null, href);
          }
        }
      });
    });
  }
}

// Image Parallax Effect
class ParallaxImages {
  constructor() {
    this.parallaxElements = document.querySelectorAll('[data-parallax]');
    if (this.parallaxElements.length > 0) {
      this.init();
    }
  }

  init() {
    this.handleScroll = HADUtils.throttle(() => {
      this.parallaxElements.forEach(el => {
        const speed = el.dataset.parallaxSpeed || 0.5;
        const rect = el.getBoundingClientRect();
        const scrolled = window.pageYOffset;
        const rate = scrolled * speed;

        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.style.transform = `translate3d(0, ${rate}px, 0)`;
        }
      });
    }, 10);

    window.addEventListener('scroll', this.handleScroll);
  }
}

// Newsletter Popup
class NewsletterPopup {
  constructor() {
    this.popup = document.querySelector('[data-newsletter-popup]');
    if (this.popup) {
      this.init();
    }
  }

  init() {
    // Check if user has already seen the popup
    if (localStorage.getItem('hadNewsletterShown')) {
      return;
    }

    // Show after 10 seconds or on exit intent
    setTimeout(() => this.show(), 10000);
    document.addEventListener('mouseleave', (e) => {
      if (e.clientY <= 0) {
        this.show();
      }
    }, { once: true });

    // Close handlers
    this.popup.querySelector('[data-close-popup]')?.addEventListener('click', () => {
      this.close();
    });

    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        this.close();
      }
    });
  }

  show() {
    if (this.shown) return;
    this.popup.style.display = 'flex';
    this.popup.style.animation = 'fadeIn 0.3s ease';
    document.body.style.overflow = 'hidden';
    this.shown = true;
  }

  close() {
    this.popup.style.animation = 'fadeOut 0.3s ease';
    setTimeout(() => {
      this.popup.style.display = 'none';
      document.body.style.overflow = '';
    }, 300);
    localStorage.setItem('hadNewsletterShown', 'true');
  }
}

// Back to Top Button
class BackToTop {
  constructor() {
    this.createButton();
  }

  createButton() {
    const button = document.createElement('button');
    button.id = 'back-to-top';
    button.innerHTML = '↑';
    button.setAttribute('aria-label', 'Back to top');
    button.style.cssText = `
      position: fixed;
      bottom: 30px;
      right: 30px;
      width: 50px;
      height: 50px;
      background: #1a1a1a;
      color: #ffffff;
      border: none;
      border-radius: 50%;
      font-size: 24px;
      cursor: pointer;
      opacity: 0;
      visibility: hidden;
      transition: all 0.3s ease;
      z-index: 999;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    `;

    document.body.appendChild(button);

    // Show/hide based on scroll position
    window.addEventListener('scroll', HADUtils.throttle(() => {
      if (window.pageYOffset > 300) {
        button.style.opacity = '1';
        button.style.visibility = 'visible';
      } else {
        button.style.opacity = '0';
        button.style.visibility = 'hidden';
      }
    }, 100));

    // Scroll to top on click
    button.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });

    // Hover effect
    button.addEventListener('mouseenter', () => {
      button.style.background = '#c9a961';
      button.style.transform = 'translateY(-3px)';
    });

    button.addEventListener('mouseleave', () => {
      button.style.background = '#1a1a1a';
      button.style.transform = 'translateY(0)';
    });
  }
}

// Product Image Zoom
class ProductImageZoom {
  constructor() {
    this.images = document.querySelectorAll('[data-zoom-image]');
    if (this.images.length > 0) {
      this.init();
    }
  }

  init() {
    this.images.forEach(img => {
      img.style.cursor = 'zoom-in';

      img.addEventListener('click', () => {
        this.openZoom(img);
      });
    });
  }

  openZoom(img) {
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.95);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: zoom-out;
      animation: fadeIn 0.3s ease;
    `;

    const zoomedImg = img.cloneNode();
    zoomedImg.style.cssText = `
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
      animation: zoomIn 0.3s ease;
    `;

    overlay.appendChild(zoomedImg);
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';

    overlay.addEventListener('click', () => {
      overlay.style.animation = 'fadeOut 0.3s ease';
      setTimeout(() => {
        overlay.remove();
        document.body.style.overflow = '';
      }, 300);
    });
  }
}

// Mobile Menu Enhancement
class MobileMenuEnhancement {
  constructor() {
    this.mobileMenu = document.querySelector('.mobile-menu');
    if (this.mobileMenu) {
      this.init();
    }
  }

  init() {
    // Add swipe to close functionality
    let touchStartX = 0;
    let touchEndX = 0;

    this.mobileMenu.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });

    this.mobileMenu.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    });
  }

  handleSwipe() {
    if (touchEndX < touchStartX - 50) {
      // Swipe left - close menu
      const closeButton = this.mobileMenu.querySelector('[data-close-menu]');
      if (closeButton) {
        closeButton.click();
      }
    }
  }
}

// Form Enhancements
class FormEnhancements {
  constructor() {
    this.forms = document.querySelectorAll('form');
    if (this.forms.length > 0) {
      this.init();
    }
  }

  init() {
    this.forms.forEach(form => {
      // Add floating labels
      this.addFloatingLabels(form);

      // Add client-side validation feedback
      this.addValidationFeedback(form);
    });
  }

  addFloatingLabels(form) {
    const inputs = form.querySelectorAll('input:not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea');

    inputs.forEach(input => {
      if (input.value) {
        input.classList.add('has-value');
      }

      input.addEventListener('blur', () => {
        if (input.value) {
          input.classList.add('has-value');
        } else {
          input.classList.remove('has-value');
        }
      });
    });
  }

  addValidationFeedback(form) {
    const inputs = form.querySelectorAll('input[required], textarea[required]');

    inputs.forEach(input => {
      input.addEventListener('invalid', (e) => {
        e.preventDefault();
        input.classList.add('invalid');
      });

      input.addEventListener('input', () => {
        input.classList.remove('invalid');
      });
    });
  }
}

// Initialize all features
class HADInit {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeAll());
    } else {
      this.initializeAll();
    }
  }

  initializeAll() {
    // Core utilities
    HADUtils.lazyLoadImages();

    // Initialize components
    new ScrollAnimations();
    new SmoothScroll();
    new ParallaxImages();
    new NewsletterPopup();
    new BackToTop();
    new ProductImageZoom();
    new MobileMenuEnhancement();
    new FormEnhancements();

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('had:initialized'));
  }
}

// Auto-initialize
new HADInit();

// Export for external use
window.HAD = {
  Utils: HADUtils,
  ScrollAnimations,
  SmoothScroll,
  ParallaxImages,
  NewsletterPopup,
  BackToTop,
  ProductImageZoom
};
